import React, { useMemo, useState } from 'react';
import H from '@hero/ui-kit/typography/H';
import Button from '@hero/ui-kit/inputs/Button';
import MarkModal from '../MarkModal';
import P from '@hero/ui-kit/typography/P';
import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import type { TemplateTargetKeys, Channel, BlockDoc } from '../../types';
import useTemplate from '../../api/useTemplate';
import useTemplateEditorContext from '../../api/useTemplateEditorContext';
import useTemplateValidate from '../../api/useTemplateValidate';
import { EditorContext } from './EditorContext';
import type { EditorContextValue } from './EditorContext';
import BlockRenderer from './BlockRenderer';
import PreviewPanel from './PreviewPanel';
import ValidateBanner from './ValidateBanner';
import VariableList from './VariableList';
import HtmlTemplateEditor from '../HtmlTemplateEditor';
type ReadOnlyProps = {
    target: TemplateTargetKeys;
    channel: Channel;
    subject?: string;
    targetKind?: string | null;
};
const ReadOnlyBlockEditor: React.FC<ReadOnlyProps> = ({ target, channel, subject, targetKind }) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const templateQuery = useTemplate(target);
    const rawTemplate = templateQuery.data?.data?.block_doc;
    const isHtml = rawTemplate?.format === 'html';
    const contextQuery = useTemplateEditorContext(target);
    const blockDoc: BlockDoc = useMemo(() => {
        const raw = templateQuery.data?.data?.block_doc;
        if (raw && 'blocks' in raw && Array.isArray(raw.blocks)) {
            return raw as BlockDoc;
        }
        return { version: 1, blocks: [] };
    }, [templateQuery.data]);
    const docHash = useMemo(() => JSON.stringify(blockDoc), [blockDoc]);
    const targetKey = useMemo(() => JSON.stringify(target), [target]);
    const validateQuery = useTemplateValidate(
        docHash,
        { block_doc: blockDoc, subject, target_kind: targetKind ?? undefined },
        templateQuery.isSuccess && !isHtml
    );
    const noop = useMemo(() => () => {}, []);
    const editorContext = useMemo(() => {
        const ctx = contextQuery.data?.data;
        return {
            variables: ctx?.variables ?? [],
            urlSlugs: ctx?.url_slugs ?? [],
            channel,
            target,
            insertTarget: null,
            setInsertTarget: noop as EditorContextValue['setInsertTarget']
        };
    }, [contextQuery.data, channel, target, noop]);
    if (templateQuery.isLoading || contextQuery.isLoading) {
        return <InlineLoader />;
    }
    if (templateQuery.error) {
        return <QueryInlineError error={templateQuery.error} />;
    }
    if (contextQuery.error) {
        return <QueryInlineError error={contextQuery.error} />;
    }
    const typeLabel = channel === 'email' && <P>Template type: {isHtml ? 'HTML' : 'Blocks'}</P>;
    if (isHtml && rawTemplate) {
        return (
            <>
                {typeLabel}
                <HtmlTemplateEditor
                    target={target}
                    targetKind={targetKind}
                    readOnly
                    initial={{
                        name: rawTemplate.name ?? '',
                        html: rawTemplate.html ?? '',
                        subject: rawTemplate.subject ?? '',
                        preheader: rawTemplate.preheader ?? ''
                    }}
                />
            </>
        );
    }
    return (
        <EditorContext.Provider value={editorContext}>
            {typeLabel}
            {channel === 'sms' && (
                <div
                    style={{
                        padding: '0.8rem 1.2rem',
                        backgroundColor: 'var(--mark-selected)',
                        borderRadius: '0.4rem',
                        marginBottom: '1.2rem'
                    }}
                >
                    <P noDefaultMargin size="small">
                        SMS template — only paragraph blocks are supported.
                    </P>
                </div>
            )}
            <ValidateBanner
                data={validateQuery.data?.data}
                isLoading={validateQuery.isLoading}
                isStale={validateQuery.isPlaceholderData}
                error={validateQuery.error}
            />
            <div
                style={{
                    display: 'flex',
                    gap: '2.4rem',
                    marginTop: '1.6rem'
                }}
            >
                <div
                    style={{
                        flex: 1,
                        minWidth: 0
                    }}
                >
                    {blockDoc.blocks.length === 0 ? (
                        <P
                            noDefaultMargin
                            styles={{
                                color: 'var(--mark-muted)',
                                padding: '2rem 0'
                            }}
                        >
                            This template has no blocks yet.
                        </P>
                    ) : (
                        <BlockRenderer blocks={blockDoc.blocks} />
                    )}
                </div>
                <div
                    style={{
                        width: 240,
                        flexShrink: 0
                    }}
                >
                    <VariableList interactive={false} />
                </div>
            </div>
            <Button variant="secondary" onClick={() => setPreviewOpen(true)}>
                Preview template
            </Button>
            {previewOpen && (
                <MarkModal aria-label="Preview template" onClose={() => setPreviewOpen(false)}>
                    <H role="h4">Preview template</H>
                    <PreviewPanel
                        key={targetKey}
                        blockDoc={blockDoc}
                        channel={channel}
                        parentSubject={subject}
                    />
                </MarkModal>
            )}
        </EditorContext.Provider>
    );
};
export default ReadOnlyBlockEditor;
